import { AsyncPipe } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RouterState } from '@angular/router';
import { Device, DeviceSyncStatus, InstalledApplication, Room } from '@era/locations-sdk';
import { CategoryStoreSelectors, DevicesRouterStoreSelectors, selectDeviceApplicationIsInUseByUdid } from '@era/stores';
import { EnvironmentService } from '@methylium/core';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DeviceInstalledApplicationsStoreSelectors } from '../../sub-features/applications-on-device/device-installed-applications-store';
import {
  DevicesOsUpdateStatusSelectors,
  DevicesOsUpdateStatusStates,
} from '../../sub-features/device-os-updates/devices-os-update-statuses-store';
import { DeviceSyncStatusSelectors } from '../../sub-features/device-sync/device-sync-statuses-store';
import { DeviceGridItemComponent } from './device-grid-item.component';

@Component({
  selector: 'era-device-grid-item',
  templateUrl: './device-grid-item.container.html',
  styleUrls: ['./device-grid-item.container.scss'],
  imports: [DeviceGridItemComponent, AsyncPipe],
})
export class DeviceGridItemContainerComponent implements OnChanges {
  @Input() public device: Device;

  public readonly hasWarning$: Observable<boolean>;
  public readonly isSelected$: Observable<boolean>;
  public readonly osUpdateStatus$: Observable<DevicesOsUpdateStatusStates.OSUpdateStatus>;
  public readonly isApplicationInSync$: Observable<boolean>;
  public readonly isInUse$: Observable<boolean>;
  public osUpdateStatus: DevicesOsUpdateStatusStates.OSUpdateStatus;

  private readonly deviceSubject$: Subject<Device> = new ReplaySubject(1);

  constructor(
    private readonly statusStore: Store<DeviceSyncStatus>,
    private readonly osUpdateStatusStore: Store<DevicesOsUpdateStatusStates.OSUpdateStatus>,
    private readonly roomStore: Store<Room>,
    private readonly routerStore: Store<RouterState>,
    private readonly applicationSyncStatusStore: Store<InstalledApplication>,
    private readonly environmentService: EnvironmentService
  ) {
    const isAppManagementEnabled = this.environmentService.environment.features.appmanagement.enabled;

    this.hasWarning$ = this.deviceSubject$.pipe(
      switchMap((x) => this.statusStore.select(DeviceSyncStatusSelectors.selectIsDeviceInCorrectSyncState(x.deviceId))),
      map((inCorrectState) => !inCorrectState)
    );

    this.isSelected$ = combineLatest([
      this.deviceSubject$,
      this.routerStore.select(DevicesRouterStoreSelectors.selectUdid),
    ]).pipe(map(([device, udid]) => device.deviceId === udid));

    this.osUpdateStatus$ = this.deviceSubject$.pipe(
      switchMap((x) =>
        this.osUpdateStatusStore.select(DevicesOsUpdateStatusSelectors.selectOsUpdateStatusWithUdidParam(x.deviceId))
      )
    );

    this.isApplicationInSync$ = this.deviceSubject$.pipe(
      switchMap((x: Device) =>
        this.applicationSyncStatusStore.select(
          DeviceInstalledApplicationsStoreSelectors.selectConfiguredApplicationsAreInstalledByUdid(x.deviceId)
        )
      )
    );

    if (!isAppManagementEnabled) {
      this.isInUse$ = of(undefined);
    } else {
      this.isInUse$ = this.deviceSubject$.pipe(
        switchMap((device: Device) => {
          if (device.roomUniqueId) {
            return this.roomStore
              .select(CategoryStoreSelectors.selectCategoryByUniqueId('Room', device.roomUniqueId))
              .pipe(map((x) => x?.entity?.isCheckedIn ?? false));
          }
          if (device.kioskUniqueId) {
            return this.statusStore.select(selectDeviceApplicationIsInUseByUdid(device.deviceId));
          }
          return of(null);
        })
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.device) {
      this.deviceSubject$.next(this.device);
    }
  }
}
